import { useState, useEffect, useMemo } from "react";
import { FiArrowLeft, FiCheck } from "react-icons/fi";
import { MdPayment } from "react-icons/md";
import { toast } from "react-toastify";
import Receipt from "../../pages/Receipt";
import PosInvoice from "../../pages/PosInvoice";

const PaymentDrawer = ({
  isOpen,
  onClose,
  onCompletePayment,
  customer = null,
  items = [],
  isPOS = false,
  totals = {
    subtotal: 0,
    disc: 0,
    gst: 0,
    net: 0,
    paid: 0,
    remaining: 0,
  },
  roundedDue: propRoundedDue = 0,
  roundOff: propRoundOff = 0,
}) => {
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    method: "",
    reference: "",
    notes: "",
  });

  // Adjustment (mirrors OrderPaymentDrawer exactly)
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);

  // Overpayment handling (mirrors OrderPaymentDrawer exactly)
  const [overpaymentAction, setOverpaymentAction] = useState(null);
  const [showOverpaymentPrompt, setShowOverpaymentPrompt] = useState(false);

  // Receipt / invoice state (original PaymentDrawer)
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showDownloadInvoice, setShowDownloadInvoice] = useState(false);

  // Only 3 methods as requested
  const paymentMethodOptions = [
    { value: "cash",  label: "CASH",             icon: "💵" },
    { value: "upi",   label: "UPI",              icon: "📱" },
    { value: "card",  label: "CREDIT/DEBIT CARD", icon: "💳" },
  ];

  const actualItems = Array.isArray(items) ? items : items ? [items] : [];

  // ── scroll lock ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // ── reset on close ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setPaymentDetails({ amount: "", method: "", reference: "", notes: "" });
      setAdjustmentAmount(0);
      setOverpaymentAction(null);
      setShowOverpaymentPrompt(false);
      setPendingPaymentData(null);
    }
  }, [isOpen]);

  // ── prevent scroll on number inputs ─────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.target.type === "number") e.preventDefault(); };
    document.addEventListener("wheel", handler, { passive: false });
    return () => document.removeEventListener("wheel", handler);
  }, []);

  // ── derived remaining — USE PROPS DIRECTLY ─────────────────────────────────
  const originalDue = useMemo(() => {
    if (isPOS && totals.rounded_remaining != null) return totals.rounded_remaining;
    return totals.remaining != null
      ? totals.remaining
      : Math.max(0, totals.net - totals.paid);
  }, [totals, isPOS]);

  // Use propRoundedDue instead of recalculating
  const roundedDue = propRoundedDue;
  const roundOffDifference = propRoundOff;

  // effectiveDue = roundedDue - adjustment
  const effectiveDue = useMemo(() => {
    const adj = parseFloat(adjustmentAmount) || 0;
    return Math.max(0, roundedDue - adj);
  }, [roundedDue, adjustmentAmount]);

  // ── overpayment detection ────────────────────────────────────────────────
  const collectedAmount = parseFloat(paymentDetails.amount) || 0;
  const isOverpayment   = collectedAmount > effectiveDue && effectiveDue > 0;

  useEffect(() => {
    if (collectedAmount > effectiveDue && effectiveDue > 0) {
      setShowOverpaymentPrompt(true);
    } else {
      setShowOverpaymentPrompt(false);
      setOverpaymentAction(null);
    }
  }, [collectedAmount, effectiveDue]);

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return `₹${num.toFixed(2)}`;
  };

  const handlePaymentMethodChange = (methodValue) => {
    setPaymentDetails((prev) => ({
      ...prev,
      method: methodValue,
      reference: methodValue === "cash" ? "" : prev.reference,
    }));
  };

  // ── submit ───────────────────────────────────────────────────────────────
  const handlePaymentComplete = () => {
    if (!paymentDetails.method) { toast.error("Select a payment method"); return; }
    if (collectedAmount <= 0)   { toast.error("Enter a valid payment amount"); return; }
    if (paymentDetails.method !== "cash" && !paymentDetails.reference.trim()) {
      toast.error("Please enter a reference number for this payment method.");
      return;
    }
    if (isOverpayment && !overpaymentAction) {
      toast.error("Please choose how to handle the overpayment (Refund or Credit).");
      return;
    }

    // How much of the collectedAmount actually covers the due
    const applied = Math.min(collectedAmount, effectiveDue);
    const excess  = collectedAmount - applied;

    const isFullPay     = applied >= effectiveDue;
    const paymentType   = isFullPay ? "full" : "partial";
    const paymentStatus = isFullPay ? "paid" : applied > 0 ? "partial" : "unpaid";

    // Partial payment needs a customer
    if (!isFullPay && !customer?._id) {
      toast.error("Cannot process partial payment without customer details.");
      return;
    }

    const payload = {
      paymentType,
      amount: applied,
      waived_amount: adjustmentAmount,
      amountPaid: collectedAmount,
      balanceRefunded: 0,
      excessAmount: excess,
      overpaymentAction,
      creditAmount: overpaymentAction === "credit" ? excess : 0,
      method: paymentDetails.method,
      reference: paymentDetails.reference,
      notes: paymentDetails.notes,
      adjustmentAmount,
      round_off: roundOffDifference,
      discountType: totals.discount_type,
      discountValue: totals.discount_value,
      round_off_details: roundOffDifference !== 0
        ? {
            applied: true,
            originalAmount: originalDue,
            roundedAmount: roundedDue,
            difference: roundOffDifference,
          }
        : null,
      totals,
      items: actualItems,
      totalCovered: applied + (parseFloat(adjustmentAmount) || 0),
      payment_status: paymentStatus,
    };

    setIsPaymentLoading(true);
    setPendingPaymentData(payload);
    onCompletePayment(payload);

    toast.success("Payment completed successfully!");
    setTimeout(() => {
      setPaymentDetails({ amount: "", method: "", reference: "", notes: "" });
      setAdjustmentAmount(0);
      setOverpaymentAction(null);
      setPendingPaymentData(null);
      setIsPaymentLoading(false);
      onClose();
    }, 1000);
  };

  // ── document helpers ─────────────────────────────────────────────────────
  const handleDocumentComplete = ({ success, file, error }) => {
    setShowReceipt(false);
    setReceiptData(null);
    setInvoiceData(null);
    if (success) {
      toast.success(`Document printed: ${file?.name}`);
      setTimeout(() => onClose(), 1000);
    } else {
      console.error(error);
      toast.error("Document printing failed");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[1000] transition-all duration-300 ease-in-out">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
          onClick={onClose}
        />

        {/* Drawer */}
        <div className="absolute right-0 top-0 h-full w-full lg:w-2/5 bg-white shadow-xl flex flex-col">

          {/* ── Header ── */}
          <div className="flex items-center p-3 border-b bg-gradient-to-r from-green-50 to-emerald-50 gap-2">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-white/70 flex items-center justify-center"
            >
              <FiArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
                <MdPayment className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800">Payment Details</h2>
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div className="flex-1 overflow-auto p-4 space-y-3">

            {/* ── Totals ── */}
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {totals.disc > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discount</span>
                  <span>- {formatCurrency(totals.disc)}</span>
                </div>
              )}
              <div className="flex justify-between text-green-600">
                <span>Tax Amount</span>
                <span>+ {formatCurrency(totals.gst)}</span>
              </div>
              
              {/* Order Amount with Round Off */}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>
                  Order Amount 
                  {roundOffDifference !== 0 && (
                    <span className={`text-xs ml-1 ${roundOffDifference > 0 ? 'text-gray-400' : 'text-gray-400'}`}>
                      (round off: {roundOffDifference > 0 ? '+' : ''}{formatCurrency(Math.abs(roundOffDifference))})
                    </span>
                  )}
                </span>
                <span>{formatCurrency(roundedDue)}</span>
              </div>
              
              {totals.paid > 0 && (
                <div className="flex justify-between text-blue-600">
                  <span>Amount Already Paid</span>
                  <span>- {formatCurrency(totals.paid)}</span>
                </div>
              )}
            </div>

            {/* ── Adjustment + Final Due side-by-side ── */}
            <div className="flex gap-2">
              <div className="flex-1 bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                <label className="block text-sm font-medium mb-1">Adjustment Amount</label>
                <input
                  type="number"
                  value={adjustmentAmount}
                  onChange={(e) =>
                    setAdjustmentAmount(Math.max(0, parseFloat(e.target.value) || 0))
                  }
                  min="0"
                  step="0.01"
                  placeholder="Enter adjustment amount"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-0.5">Subtracted from rounded due</p>
              </div>
              <div className="flex-1 flex flex-col bg-green-50 p-2 rounded-lg border border-green-200 justify-center items-center">
                <div className="text-xs font-medium text-green-800">Final Amount to Pay</div>
                <div className="text-xl font-bold text-green-700">{formatCurrency(effectiveDue)}</div>
              </div>
            </div>

            {/* ── Payment Method ── */}
            <div>
              <label className="block text-sm font-medium mb-2">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                {paymentMethodOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all
                      ${paymentDetails.method === option.value
                        ? "border-green-500 bg-green-50 ring-1 ring-green-200"
                        : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={option.value}
                      checked={paymentDetails.method === option.value}
                      onChange={() => handlePaymentMethodChange(option.value)}
                      className="w-3.5 h-3.5 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-xs font-medium text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ── Amount Collected ── */}
            {paymentDetails.method && (
              <div>
                <label className="block text-sm font-medium mb-1">Amount Collected</label>
                <input
                  type="number"
                  value={paymentDetails.amount}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  placeholder="Enter amount received"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500"
                  step="0.01"
                  min="0"
                />
              </div>
            )}

            {/* ── Overpayment prompt ── */}
            {showOverpaymentPrompt && (
              <div className="p-2 border border-yellow-300 bg-yellow-50 rounded-lg text-sm">
                <p className="font-medium text-yellow-800 mb-1">
                  Amount exceeds due. How to handle excess?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOverpaymentAction("refund")}
                    className={`px-2 py-0.5 text-sm rounded ${overpaymentAction === "refund" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                  >
                    Refund
                  </button>
                  <button
                    onClick={() => setOverpaymentAction("credit")}
                    className={`px-2 py-0.5 text-sm rounded ${overpaymentAction === "credit" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                  >
                    Add as Credit
                  </button>
                </div>
                {overpaymentAction && (
                  <p className="text-xs mt-1">
                    Excess: {formatCurrency(collectedAmount - effectiveDue)} will be{" "}
                    {overpaymentAction === "refund" ? "refunded" : "credited"}.
                  </p>
                )}
              </div>
            )}

            {/* ── Reference for non-cash ── */}
            {paymentDetails.method && paymentDetails.method !== "cash" && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Reference Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={paymentDetails.reference}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({ ...prev, reference: e.target.value }))
                  }
                  placeholder={
                    paymentDetails.method === "card"
                      ? "Card Reference / Auth Code"
                      : "Transaction ID"
                  }
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500"
                />
              </div>
            )}

            {/* ── Notes ── */}
            {paymentDetails.method && (
              <div>
                <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                <textarea
                  value={paymentDetails.notes}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  rows={1}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500"
                  placeholder="Add payment notes..."
                />
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="p-3 border-t bg-white flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handlePaymentComplete}
              disabled={
                !paymentDetails.method ||
                isPaymentLoading ||
                !paymentDetails.amount ||
                (isOverpayment && !overpaymentAction)
              }
              className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
            >
              {isPaymentLoading ? (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
              ) : (
                <FiCheck className="w-4 h-4" />
              )}
              {isPaymentLoading ? "Processing..." : "Complete Payment"}
            </button>
          </div>
        </div>
      </div>

      {/* Receipt Component */}
      {showReceipt && receiptData && (
        <Receipt
          shouldDownloadPdf={true}
          receipt={receiptData}
          onSavePdfComplete={handleDocumentComplete}
        />
      )}

      {/* POS Invoice Component */}
      {showDownloadInvoice && invoiceData && (
        <PosInvoice
          shouldDownloadPdf={showDownloadInvoice}
          invoice={invoiceData}
          onSavePdfComplete={({ success, file, error }) => {
            setShowDownloadInvoice(false);
            if (success) {
              toast.success("Invoice downloaded successfully!");
              setTimeout(() => onClose(), 1000);
            } else {
              toast.error("Invoice download failed");
              setIsPaymentLoading(false);
            }
          }}
        />
      )}
    </>
  );
};

export default PaymentDrawer;